import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  FileText, 
  MessageSquare, 
  Activity, 
  TrendingUp, 
  Clock,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react';

interface DashboardStats {
  users: {
    byRole: {
      [key: string]: {
        total: number;
        active: number;
        recentRegistrations: number;
      };
    };
    totals: {
      total: number;
      active: number;
      recentRegistrations: number;
    };
  };
  cases: {
    byStatus: {
      [key: string]: {
        total: number;
        recent: number;
      };
    };
    totals: {
      total: number;
      recent: number;
    };
  };
  forum: {
    activeCategories: number;
    totalPosts: number;
    recentPosts: number;
    totalReplies: number;
    recentReplies: number;
  };
  systemHealth: {
    activeUsers24h: number;
    cases24h: number;
    comments24h: number;
    timestamp: string;
  };
}

interface StatCardProps {
  title: string;
  value: number;
  change?: number;
  changeLabel?: string;
  icon: React.ReactNode;
  color: string;
  trend?: 'up' | 'down' | 'neutral';
}

function StatCard({ title, value, change, changeLabel, icon, color, trend }: StatCardProps) {
  const getTrendColor = () => {
    if (!trend) return 'text-gray-500 dark:text-gray-400';
    switch (trend) {
      case 'up': return 'text-green-600 dark:text-green-400';
      case 'down': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-500 dark:text-gray-400';
    }
  };

  const getTrendIcon = () => {
    if (!trend || trend === 'neutral') return null;
    return trend === 'up' ? '↗' : '↘';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 transition-colors duration-300">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400 transition-colors duration-300">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1 transition-colors duration-300">{value.toLocaleString()}</p>
          {change !== undefined && (
            <div className={`flex items-center mt-2 text-sm ${getTrendColor()} transition-colors duration-300`}>
              <span className="mr-1">{getTrendIcon()}</span>
              <span>{change}</span>
              {changeLabel && <span className="text-gray-500 dark:text-gray-400 ml-1 transition-colors duration-300">{changeLabel}</span>}
            </div>
          )}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

interface RoleStatsProps {
  roleData: {
    [key: string]: {
      total: number;
      active: number;
      recentRegistrations: number;
    };
  };
}

function RoleStats({ roleData }: RoleStatsProps) {
  const roleLabels: { [key: string]: { label: string; icon: string; color: string } } = {
    doctor: { label: 'Γιατροί', icon: '🩺', color: 'bg-blue-100 text-blue-800' },
    consultant_emeritus: { label: 'Ομότιμοι Σύμβουλοι', icon: '🩺✨', color: 'bg-purple-100 text-purple-800' },
    moderator: { label: 'Συντονιστές', icon: '💉🔍', color: 'bg-orange-100 text-orange-800' },
    admin: { label: 'Διαχειριστές', icon: '🧬⚙️', color: 'bg-red-100 text-red-800' }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 transition-colors duration-300">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 transition-colors duration-300">Κατανομή Χρηστών ανά Ρόλο</h3>
      <div className="space-y-4">
        {Object.entries(roleData).map(([role, stats]) => {
          const roleInfo = roleLabels[role];
          if (!roleInfo) return null;

          return (
            <div key={role} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg transition-colors duration-300">
              <div className="flex items-center space-x-3">
                <span className="text-lg">{roleInfo.icon}</span>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white transition-colors duration-300">{roleInfo.label}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">
                    {stats.active} ενεργοί από {stats.total} συνολικά
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${roleInfo.color}`}>
                  {stats.total}
                </div>
                {stats.recentRegistrations > 0 && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1 transition-colors duration-300">
                    +{stats.recentRegistrations} νέοι (30 ημέρες)
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface SystemHealthProps {
  healthData: {
    activeUsers24h: number;
    cases24h: number;
    comments24h: number;
    timestamp: string;
  };
}

function SystemHealth({ healthData }: SystemHealthProps) {
  const healthIndicators = [
    {
      label: 'Ενεργοί Χρήστες (24ω)',
      value: healthData.activeUsers24h,
      threshold: 10,
      icon: <Users className="w-4 h-4" />
    },
    {
      label: 'Νέα Περιστατικά (24ω)',
      value: healthData.cases24h,
      threshold: 5,
      icon: <FileText className="w-4 h-4" />
    },
    {
      label: 'Νέα Σχόλια (24ω)',
      value: healthData.comments24h,
      threshold: 20,
      icon: <MessageSquare className="w-4 h-4" />
    }
  ];

  const getHealthStatus = (value: number, threshold: number) => {
    if (value >= threshold) return { status: 'healthy', color: 'text-green-600', icon: <CheckCircle className="w-4 h-4" /> };
    if (value >= threshold * 0.5) return { status: 'warning', color: 'text-yellow-600', icon: <AlertTriangle className="w-4 h-4" /> };
    return { status: 'critical', color: 'text-red-600', icon: <XCircle className="w-4 h-4" /> };
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 transition-colors duration-300">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white transition-colors duration-300">Υγεία Συστήματος</h3>
        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">
          <Clock className="w-4 h-4 mr-1" />
          {new Date(healthData.timestamp).toLocaleString('el-GR')}
        </div>
      </div>
      <div className="space-y-3">
        {healthIndicators.map((indicator, index) => {
          const health = getHealthStatus(indicator.value, indicator.threshold);
          return (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg transition-colors duration-300">
              <div className="flex items-center space-x-3">
                {indicator.icon}
                <span className="font-medium text-gray-900 dark:text-white transition-colors duration-300">{indicator.label}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="font-bold text-gray-900 dark:text-white transition-colors duration-300">{indicator.value}</span>
                <div className={health.color}>
                  {health.icon}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      const response = await fetch('/api/v1/admin/dashboard', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Αποτυχία λήψης στατιστικών');
      }

      const result = await response.json();
      setStats(result.data);
    } catch (error) {
      console.error('Dashboard stats error:', error);
      setError(error instanceof Error ? error.message : 'Άγνωστο σφάλμα');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-600"></div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 transition-colors duration-300">
        <div className="flex items-center">
          <XCircle className="w-5 h-5 text-red-400 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-red-800 dark:text-red-300 transition-colors duration-300">Σφάλμα φόρτωσης</h3>
            <p className="text-sm text-red-700 dark:text-red-400 mt-1 transition-colors duration-300">{error || 'Αποτυχία λήψης στατιστικών dashboard'}</p>
          </div>
        </div>
        <button
          onClick={fetchDashboardStats}
          className="mt-4 bg-red-100 hover:bg-red-200 dark:bg-red-800/20 dark:hover:bg-red-800/30 text-red-800 dark:text-red-300 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-300"
        >
          Δοκιμή ξανά
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 transition-colors duration-300">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white transition-colors duration-300">Πίνακας Διαχείρισης</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1 transition-colors duration-300">Επισκόπηση της πλατφόρμας Pathiety</p>
          </div>
          <div className="flex items-center space-x-2">
            <Shield className="w-6 h-6 text-medical-600" />
            <span className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">Admin Panel</span>
          </div>
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Συνολικοί Χρήστες"
          value={stats.users.totals.total}
          change={stats.users.totals.recentRegistrations}
          changeLabel="νέοι (30 ημέρες)"
          icon={<Users className="w-6 h-6 text-white" />}
          color="bg-blue-500"
          trend={stats.users.totals.recentRegistrations > 0 ? 'up' : 'neutral'}
        />
        
        <StatCard
          title="Ενεργοί Χρήστες"
          value={stats.users.totals.active}
          icon={<Activity className="w-6 h-6 text-white" />}
          color="bg-green-500"
        />
        
        <StatCard
          title="Συνολικά Περιστατικά"
          value={stats.cases.totals.total}
          change={stats.cases.totals.recent}
          changeLabel="νέα (7 ημέρες)"
          icon={<FileText className="w-6 h-6 text-white" />}
          color="bg-medical-500"
          trend={stats.cases.totals.recent > 0 ? 'up' : 'neutral'}
        />
        
        <StatCard
          title="Δημοσιεύσεις Φόρουμ"
          value={stats.forum.totalPosts}
          change={stats.forum.recentPosts}
          changeLabel="νέες (7 ημέρες)"
          icon={<MessageSquare className="w-6 h-6 text-white" />}
          color="bg-purple-500"
          trend={stats.forum.recentPosts > 0 ? 'up' : 'neutral'}
        />
      </div>

      {/* Case Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Object.entries(stats.cases.byStatus).map(([status, data]) => {
          let statusConfig = {
            label: status,
            color: 'bg-gray-500',
            icon: <FileText className="w-6 h-6 text-white" />
          };

          switch (status) {
            case 'open':
              statusConfig = {
                label: 'Ανοιχτά',
                color: 'bg-blue-500',
                icon: <FileText className="w-6 h-6 text-white" />
              };
              break;
            case 'resolved':
              statusConfig = {
                label: 'Επιλυμένα',
                color: 'bg-green-500',
                icon: <CheckCircle className="w-6 h-6 text-white" />
              };
              break;
            case 'archived':
              statusConfig = {
                label: 'Αρχειοθετημένα',
                color: 'bg-gray-500',
                icon: <Activity className="w-6 h-6 text-white" />
              };
              break;
          }

          return (
            <StatCard
              key={status}
              title={`Περιστατικά ${statusConfig.label}`}
              value={data.total}
              change={data.recent}
              changeLabel="νέα (7 ημέρες)"
              icon={statusConfig.icon}
              color={statusConfig.color}
              trend={data.recent > 0 ? 'up' : 'neutral'}
            />
          );
        })}
      </div>

      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RoleStats roleData={stats.users.byRole} />
        <SystemHealth healthData={stats.systemHealth} />
      </div>

      {/* Forum Activity */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 transition-colors duration-300">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 transition-colors duration-300">Δραστηριότητα Φόρουμ</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg transition-colors duration-300">
            <p className="text-2xl font-bold text-gray-900 dark:text-white transition-colors duration-300">{stats.forum.activeCategories}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors duration-300">Ενεργές Κατηγορίες</p>
          </div>
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg transition-colors duration-300">
            <p className="text-2xl font-bold text-gray-900 dark:text-white transition-colors duration-300">{stats.forum.totalReplies}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors duration-300">Συνολικές Απαντήσεις</p>
          </div>
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg transition-colors duration-300">
            <p className="text-2xl font-bold text-gray-900 dark:text-white transition-colors duration-300">{stats.forum.recentReplies}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors duration-300">Νέες Απαντήσεις (7 ημέρες)</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 transition-colors duration-300">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 transition-colors duration-300">Γρήγορες Ενέργειες</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button 
            onClick={() => navigate('/admin/users')}
            className="flex items-center justify-center p-4 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg transition-colors duration-300"
          >
            <Users className="w-5 h-5 mr-2" />
            Διαχείριση Χρηστών
          </button>
          <button 
            onClick={() => navigate('/admin/cases')}
            className="flex items-center justify-center p-4 bg-medical-50 hover:bg-medical-100 dark:bg-medical-900/20 dark:hover:bg-medical-900/30 text-medical-700 dark:text-medical-300 rounded-lg transition-colors duration-300"
          >
            <FileText className="w-5 h-5 mr-2" />
            Επισκόπηση Περιστατικών
          </button>
          <button 
            onClick={() => navigate('/admin/forum')}
            className="flex items-center justify-center p-4 bg-purple-50 hover:bg-purple-100 dark:bg-purple-900/20 dark:hover:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg transition-colors duration-300"
          >
            <MessageSquare className="w-5 h-5 mr-2" />
            Συντονισμός Φόρουμ
          </button>
        </div>
      </div>
    </div>
  );
}